/**
 * InfinitSales - Controle do autopilot sem ambiguidade (v2, 2026-10-14)
 *
 * Antes havia dois interruptores que gravavam o MESMO campo autopilot_mode:
 * "Responder novos contatos" (off/all) e "Horários programados"
 * (off/scheduled). Ligar o segundo apagava o primeiro na tela e o cliente
 * desligava e religava até quebrar a configuração (a Bigens fez três PUT em
 * 20 s no dia 14/10, e cada religada carimba autopilot_since, o que muda quem
 * o agente atende).
 *
 * Agora: UM interruptor principal (ligado = all ou scheduled; desligado = off)
 * e, abaixo dele, o alcance por rádio: "Sempre" (all) ou "Só nos dias e
 * horários abaixo" (scheduled). Trocar o alcance nunca passa por `off`, então
 * o carimbo de "só novos contatos" fica onde estava (regra do backend em
 * UserManager.update_autopilot_config).
 *
 * Componente controlado e sem rede: quem usa recebe `onChangeMode(mode)` e
 * grava. Vale para a tela Agentes e para o drawer do agente do membro.
 */
import React from 'react';
import { Clock } from 'lucide-react';

export const ACTIVE_MODES = ['all', 'scheduled'];

export function isAutopilotOn(mode) {
  return ACTIVE_MODES.includes(mode);
}

/** Próximo modo ao mexer no interruptor principal. Lembra o alcance anterior. */
export function nextModeOnToggle(mode, lastScope = 'all') {
  if (isAutopilotOn(mode)) return 'off';
  return lastScope === 'scheduled' ? 'scheduled' : 'all';
}

const AutopilotModeControl = ({
  mode = 'off',
  onChangeMode,
  saving = false,
  colors,
  styles = {},
  scheduleEditor = null,
  title = 'Agente responde novos contatos',
  compact = false,
  // DIV-31 (2026-10-20): carimbo atual e o clique "a partir de agora".
  since = null,
  onResetSince = null,
}) => {
  const on = isAutopilotOn(mode);
  const scope = mode === 'scheduled' ? 'scheduled' : 'all';

  const s = {
    row: styles.toggleRow || {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: compact ? '10px 0' : '16px 20px',
      backgroundColor: compact ? 'transparent' : colors.bgSecondary,
      borderRadius: '12px', marginBottom: '8px',
    },
    label: styles.toggleLabel || { display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, marginRight: '16px' },
    title: styles.toggleTitle || { fontSize: compact ? '13px' : '15px', fontWeight: 600, color: colors.textPrimary },
    desc: styles.toggleDesc || { fontSize: '12px', color: colors.textSecondary, lineHeight: 1.4 },
    switch: styles.toggleSwitch || {
      width: '50px', height: '28px', borderRadius: '14px', padding: '2px', cursor: 'pointer',
      transition: 'background-color 0.2s', border: 'none', flexShrink: 0,
    },
    knob: styles.toggleKnob || {
      width: '24px', height: '24px', borderRadius: '50%', backgroundColor: 'white',
      transition: 'transform 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    },
    radioBox: {
      display: 'flex', flexDirection: 'column', gap: '6px',
      padding: compact ? '0 0 8px' : '0 20px 12px',
      backgroundColor: compact ? 'transparent' : colors.bgSecondary,
      borderRadius: '12px', marginTop: '-8px', marginBottom: '8px',
    },
    radio: (active) => ({
      display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 12px',
      borderRadius: '10px', textAlign: 'left', cursor: saving ? 'progress' : 'pointer',
      border: `1.5px solid ${active ? colors.accentPrimary : colors.borderColor}`,
      backgroundColor: active ? `${colors.accentPrimary}12` : 'transparent',
      opacity: saving ? 0.7 : 1,
    }),
    dot: (active) => ({
      width: '16px', height: '16px', borderRadius: '50%', flexShrink: 0, marginTop: '1px',
      border: `2px solid ${active ? colors.accentPrimary : colors.borderColor}`,
      backgroundColor: active ? colors.accentPrimary : 'transparent',
      boxShadow: active ? 'inset 0 0 0 3px #fff' : 'none',
    }),
    radioTitle: (active) => ({ fontSize: '13px', fontWeight: 600, color: active ? colors.accentPrimary : colors.textPrimary }),
    radioDesc: { fontSize: '11.5px', color: colors.textSecondary, lineHeight: 1.35, marginTop: '2px' },
  };

  const change = (next) => {
    if (saving || next === mode) return;
    onChangeMode?.(next);
  };

  return (
    <div data-testid="autopilot-mode-control">
      <div style={s.row}>
        <div style={s.label}>
          <span style={s.title}>{title}</span>
          <span style={s.desc}>
            {on
              ? 'Ligado: quem entrar em contato a partir de agora é atendido pelo agente. Quem já está no inbox continua com a equipe, a menos que seja transferido para o agente.'
              : 'Desligado: o agente só atende os contatos transferidos para ele.'}
          </span>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={on}
          aria-label={title}
          data-testid="autopilot-main-switch"
          onClick={() => change(nextModeOnToggle(mode, scope))}
          style={{
            ...s.switch,
            backgroundColor: on ? colors.accentPrimary : colors.borderColor,
            opacity: saving ? 0.6 : 1,
            pointerEvents: saving ? 'none' : 'auto',
          }}
        >
          <div style={{ ...s.knob, transform: on ? 'translateX(22px)' : 'translateX(0px)' }} />
        </button>
      </div>

      {on && onResetSince && (
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: compact ? '6px 0' : '8px 20px', cursor: saving ? 'wait' : 'pointer' }}>
          <input
            type="checkbox"
            checked={false}
            disabled={saving}
            data-testid="autopilot-reset-since"
            onChange={() => onResetSince()}
            style={{ marginTop: 3 }}
          />
          <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: compact ? '13px' : '14px', fontWeight: 600, color: colors.textPrimary }}>
              Atender todos os contatos a partir de agora
            </span>
            <span style={s.desc}>
              Marca este momento como o início: quem escrever a partir de agora é atendido pelo agente;
              quem já estava no inbox continua com a equipe, a menos que seja transferido.
              {since ? ` Marco atual: ${new Date(since).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}.` : ''}
            </span>
          </span>
        </label>
      )}

      {on && (
        <div style={s.radioBox} role="radiogroup" aria-label="Quando o agente responde">
          <button
            type="button"
            role="radio"
            aria-checked={scope === 'all'}
            data-testid="autopilot-scope-all"
            onClick={() => change('all')}
            style={s.radio(scope === 'all')}
          >
            <span style={s.dot(scope === 'all')} />
            <span>
              <div style={s.radioTitle(scope === 'all')}>Sempre</div>
              <div style={s.radioDesc}>Em qualquer dia e horário, inclusive fora do expediente.</div>
            </span>
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={scope === 'scheduled'}
            data-testid="autopilot-scope-scheduled"
            onClick={() => change('scheduled')}
            style={s.radio(scope === 'scheduled')}
          >
            <span style={s.dot(scope === 'scheduled')} />
            <span>
              <div style={s.radioTitle(scope === 'scheduled')}>
                <Clock size={13} style={{ marginRight: '5px', verticalAlign: 'middle' }} />
                Só nos dias e horários abaixo
              </div>
              <div style={s.radioDesc}>Fora deles vale a regra de fora do horário, definida no editor.</div>
            </span>
          </button>
          {scope === 'scheduled' && scheduleEditor}
        </div>
      )}
    </div>
  );
};

export default AutopilotModeControl;
